import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { siteTemplatesApi } from '../../api/client';
import type { SiteTemplate } from '../../schemas/validation';
import { cn } from '../../utils/cn';

interface TemplatePreviewPanelProps {
  template: SiteTemplate;
  onClose: () => void;
}

interface PreviewData {
  page: {
    index: number;
    title: string;
    slug: string;
    components: any[];
    showLayout?: boolean;
  };
  layout?: {
    id: string;
    name: string;
    components: any[] | null;
    isDefault: boolean;
  } | null;
  totalPages: number;
  allPages: Array<{ index: number; title: string; slug: string }>;
}

export const TemplatePreviewPanel: React.FC<TemplatePreviewPanelProps> = ({
  template,
  onClose,
}) => {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPreview = useCallback(
    async (pageIndex: number) => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await siteTemplatesApi.previewTemplate(template.id, pageIndex);
        setPreviewData({
          page: response.page,
          layout: response.layout,
          totalPages: response.totalPages,
          allPages: response.allPages,
        });
      } catch (err) {
        console.error('Failed to load template preview:', err);
        setError('Failed to load preview');
      } finally {
        setIsLoading(false);
      }
    },
    [template.id]
  );

  useEffect(() => {
    loadPreview(0);
  }, [loadPreview]);

  const handlePageChange = (index: number) => {
    setCurrentPageIndex(index);
    loadPreview(index);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{template.name}</h2>
            <p className="text-sm text-gray-500">
              {template.siteData.pages.length} page(s) &middot; Theme:{' '}
              {template.siteData.theme || 'Default'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Page Navigation */}
        {previewData && previewData.allPages.length > 1 && (
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
            <button
              onClick={() => handlePageChange(Math.max(0, currentPageIndex - 1))}
              disabled={currentPageIndex === 0 || isLoading}
              className={cn(
                'btn-ghost text-xs flex items-center',
                currentPageIndex === 0 && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ChevronLeft size={14} className="mr-1" />
              Previous
            </button>
            <div className="flex items-center space-x-2">
              {previewData.allPages.map((page, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i)}
                  disabled={isLoading}
                  className={cn(
                    'px-3 py-1 text-xs rounded-md transition-colors',
                    i === currentPageIndex
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  )}
                >
                  {page.title}
                </button>
              ))}
            </div>
            <button
              onClick={() =>
                handlePageChange(Math.min(previewData.totalPages - 1, currentPageIndex + 1))
              }
              disabled={currentPageIndex === previewData.totalPages - 1 || isLoading}
              className={cn(
                'btn-ghost text-xs flex items-center',
                currentPageIndex === previewData.totalPages - 1 && 'opacity-50 cursor-not-allowed'
              )}
            >
              Next
              <ChevronRight size={14} className="ml-1" />
            </button>
          </div>
        )}

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
                <p className="text-gray-600">Loading preview...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
              <button onClick={() => loadPreview(currentPageIndex)} className="btn-secondary mt-4">
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && previewData && (
            <div className="space-y-4">
              {/* Page Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Page:</span>{' '}
                    <span className="font-medium">{previewData.page.title}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Slug:</span>{' '}
                    <span className="font-medium">/{previewData.page.slug}</span>
                  </div>
                  {previewData.layout && (
                    <div>
                      <span className="text-gray-500">Layout:</span>{' '}
                      <span className="font-medium">{previewData.layout.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Components List */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Components ({previewData.page.components?.length || 0})
                </h4>
                {previewData.page.components && previewData.page.components.length > 0 ? (
                  <div className="space-y-2">
                    {previewData.page.components.map((component: any, index: number) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-xs text-gray-400 font-mono w-6">#{index + 1}</span>
                          <span className="text-sm font-medium text-gray-900">
                            {component.type || component.componentType || 'Unknown'}
                          </span>
                        </div>
                        {component.variant && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {component.variant}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No components on this page</p>
                )}
              </div>

              {/* Layout Components */}
              {previewData.layout && previewData.layout.components && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Layout Components ({(previewData.layout.components as any[]).length})
                  </h4>
                  <div className="space-y-2">
                    {(previewData.layout.components as any[]).map(
                      (component: any, index: number) => (
                        <div
                          key={index}
                          className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-100"
                        >
                          <span className="text-xs text-blue-400 font-mono w-6 mr-3">
                            #{index + 1}
                          </span>
                          <span className="text-sm font-medium text-blue-900">
                            {component.type || component.componentType || 'Unknown'}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
